import React, { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { DollarSign, Plus, Trash2, Loader2, RotateCcw, TrendingUp, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import api from '../../lib/api';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

export default function CommissionModal({ open, onOpenChange, staff, staffEmail, staffName, onUpdated, onNotesSaved }) {
  const [commissions, setCommissions] = useState([]);
  const [clawbacks, setClawbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  // New one-time form
  const [otAmount, setOtAmount] = useState('');
  const [otReason, setOtReason] = useState('');
  const [otSaving, setOtSaving] = useState(false);

  // New recurring form
  const [rcAmount, setRcAmount] = useState('');
  const [rcReason, setRcReason] = useState('');
  const [rcDuration, setRcDuration] = useState('3');
  const [rcSaving, setRcSaving] = useState(false);

  // New clawback form
  const [cbTotal, setCbTotal] = useState('');
  const [cbCycles, setCbCycles] = useState('4');
  const [cbReason, setCbReason] = useState('');
  const [cbSaving, setCbSaving] = useState(false);
  const [cbCommOnly, setCbCommOnly] = useState(false);

  const [deleting, setDeleting] = useState(null);
  const [commissionNotes, setCommissionNotes] = useState('');
  const initialNotesRef = useRef('');
  const otAmountRef = useRef(null);

  // Reset ALL state when staff changes — seed from parent prop immediately
  useEffect(() => {
    const propNotes = staff?.commission_notes || '';
    setCommissionNotes(propNotes);
    initialNotesRef.current = propNotes;
    setCommissions([]);
    setClawbacks([]);
    setOtAmount('');
    setOtReason('');
    setRcAmount('');
    setRcReason('');
    setRcDuration('3');
  }, [staffEmail, staff]);

  const load = useCallback(async () => {
    if (!staffEmail) return;
    try {
      setLoading(true);
      // DO NOT blank commissionNotes here — the useEffect already seeded from staff prop.
      // Blanking here was causing the ghost-save on blur during loading.
      const [cRes, cbRes] = await Promise.all([
        api.get(`/api/payroll/commissions/${encodeURIComponent(staffEmail)}`),
        api.get(`/api/payroll/clawbacks/${encodeURIComponent(staffEmail)}`),
      ]);
      setCommissions(cRes.data.data || []);
      setClawbacks(cbRes.data.data || []);
    } catch {
      toast.error('Failed to load commission data');
    } finally {
      setLoading(false);
    }
  }, [staffEmail, staff]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Auto-focus the one-time amount field when modal opens and data loads
  useEffect(() => {
    if (open && !loading && otAmountRef.current) {
      const timer = setTimeout(() => otAmountRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [open, loading]);

  // Derived data
  const oneTimeItems = commissions.filter(c => c.type === 'one_time' && !c.is_paid);
  const paidOneTime = commissions.filter(c => c.type === 'one_time' && c.is_paid);
  const recurringItems = commissions.filter(c => c.type === 'recurring');

  const addOneTime = async () => {
    const amt = parseFloat(otAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setOtSaving(true);
    try {
      await api.post('/api/payroll/commissions', {
        staff_email: staffEmail,
        type: 'one_time',
        amount: amt,
        reason: otReason || 'One-time commission',
      });
      setOtAmount('');
      setOtReason('');
      load();
      if (onUpdated) onUpdated();
      toast.success('One-time commission added');
    } catch { toast.error('Failed to add commission'); }
    finally { setOtSaving(false); }
  };

  const addRecurring = async () => {
    const amt = parseFloat(rcAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setRcSaving(true);
    try {
      await api.post('/api/payroll/commissions', {
        staff_email: staffEmail,
        type: 'recurring',
        amount: amt,
        reason: rcReason || 'Recurring commission',
        duration_months: rcDuration === 'continual' ? null : parseInt(rcDuration, 10),
      });
      setRcAmount('');
      setRcReason('');
      setRcDuration('3');
      load();
      if (onUpdated) onUpdated();
      toast.success('Recurring commission added');
    } catch { toast.error('Failed to add commission'); }
    finally { setRcSaving(false); }
  };

  const addClawback = async () => {
    const total = parseFloat(cbTotal);
    const cycles = parseInt(cbCycles, 10);
    if (!total || total <= 0) { toast.error('Enter a valid total amount'); return; }
    if (cycles < 1 || cycles > 6) { toast.error('Cycles must be 1-6'); return; }
    setCbSaving(true);
    try {
      await api.post('/api/payroll/clawbacks', {
        staff_email: staffEmail,
        total_amount: total,
        cycles,
        reason: cbReason || 'Clawback',
        take_from_commissions_only: cbCommOnly,
      });
      setCbTotal('');
      setCbCycles('4');
      setCbReason('');
      setCbCommOnly(false);
      load();
      if (onUpdated) onUpdated();
      toast.success('Clawback created');
    } catch { toast.error('Failed to create clawback'); }
    finally { setCbSaving(false); }
  };

  const deleteItem = async (type, id) => {
    setDeleting(id);
    try {
      if (type === 'commission') {
        await api.delete(`/api/payroll/commissions/${id}`);
      } else {
        await api.delete(`/api/payroll/clawbacks/${id}`);
      }
      load();
      if (onUpdated) onUpdated();
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(null); }
  };

  const oneTimeSum = oneTimeItems.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const recurringSum = recurringItems.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const clawbackSum = clawbacks.reduce((s, cb) => s + parseFloat(cb.amount_per_cycle || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl p-0 gap-0 flex flex-col" style={{ maxHeight: '85vh' }}>
        <DialogHeader className="px-6 pt-5 pb-3 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2" data-testid="commission-modal-title">
            <DollarSign className="h-5 w-5 text-primary" />
            Commissions & Deductions
          </DialogTitle>
          <p className="text-xs text-muted-foreground">{staffName} · {staffEmail}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-5">
          {/* Commission Structure Notes */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1 block">Commission Structure Notes</label>
            <textarea
              className="w-full text-xs p-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none h-[50px]"
              placeholder="e.g. 10% on new clients, $50/mo referral bonus, reviewed quarterly..."
              value={commissionNotes}
              onChange={(e) => setCommissionNotes(e.target.value)}
              onBlur={async () => {
                // FIX #1: Guard against ghost saves — skip if value equals the parent's prop
                const initialPropValue = staff?.commission_notes || '';
                if (commissionNotes === initialPropValue || (commissionNotes === '' && initialNotesRef.current !== '')) {
                  console.log("[Notes Save] Skipped — value unchanged from staff prop:", JSON.stringify(commissionNotes));
                  return;
                }
                const endpointUrl = `/api/ops/staff/${encodeURIComponent(staffEmail)}`;
                const payload = { commission_notes: commissionNotes };
                console.log("[Notes Save] Sending to URL:", endpointUrl, "Payload:", payload);
                try {
                  const res = await api.patch(endpointUrl, payload);
                  console.log("[Notes Save] Response status:", res.status, "Data:", res.data);
                  if (res.status === 200) {
                    initialNotesRef.current = commissionNotes;
                    toast.success('Note saved');
                    // Force parent to refresh payroll data so staff prop is rebuilt from DB
                    if (onNotesSaved) onNotesSaved();
                  }
                } catch (err) {
                  const errorText = err.response?.data ? JSON.stringify(err.response.data) : err.message;
                  console.error("[Notes Save Error] Server responded:", errorText, "Status:", err.response?.status);
                  toast.error('Failed to save notes');
                }
              }}
            />
          </div>

          {loading ? (
            <div className="py-10 text-center">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Loading...</p>
            </div>
          ) : (
            <>
              {/* ── SECTION 1: ONE-TIME COMMISSIONS ── */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                  <h3 className="text-sm font-semibold">One-Time Commissions</h3>
                  {oneTimeSum > 0 && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">+{fmt(oneTimeSum)}</Badge>}
                </div>
                <p className="text-[10px] text-muted-foreground mb-2">Added to gross wage this week. Cleared after Pay is confirmed.</p>

                {oneTimeItems.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {oneTimeItems.map(c => (
                      <div key={c.id} className="flex items-center justify-between py-1 px-2 rounded bg-emerald-50/50 border border-emerald-100" data-testid={`ot-item-${c.id}`}>
                        <div>
                          <span className="text-sm font-medium text-emerald-700" data-role="mono">{fmt(c.amount)}</span>
                          <span className="text-xs text-muted-foreground ml-2">{c.reason}</span>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500" onClick={() => deleteItem('commission', c.id)} disabled={deleting === c.id}>
                          {deleting === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Input ref={otAmountRef} type="number" min="0" step="0.01" placeholder="Amount" className="h-8 text-xs" value={otAmount} onChange={e => setOtAmount(e.target.value)} data-testid="ot-amount" />
                  </div>
                  <div className="flex-[2]">
                    <Input placeholder="Reason" className="h-8 text-xs" value={otReason} onChange={e => setOtReason(e.target.value)} data-testid="ot-reason" onKeyDown={e => { if (e.key === 'Enter') addOneTime(); }} />
                  </div>
                  <Button size="sm" className="h-8 px-3" onClick={addOneTime} disabled={otSaving} data-testid="ot-add-btn">
                    {otSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  </Button>
                </div>
              </div>

              <Separator />

              {/* ── SECTION 2: RECURRING COMMISSIONS ── */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <RotateCcw className="h-4 w-4 text-blue-600" />
                  <h3 className="text-sm font-semibold">Recurring Commissions</h3>
                  {recurringSum > 0 && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">+{fmt(recurringSum)}/wk</Badge>}
                </div>
                <p className="text-[10px] text-muted-foreground mb-2">Added to gross wage every week. Default duration: 3 months.</p>

                {recurringItems.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {recurringItems.map(c => (
                      <div key={c.id} className="flex items-center justify-between py-1 px-2 rounded bg-blue-50/50 border border-blue-100" data-testid={`rc-item-${c.id}`}>
                        <div>
                          <span className="text-sm font-medium text-blue-700" data-role="mono">{fmt(c.amount)}</span>
                          <span className="text-xs text-muted-foreground ml-2">{c.reason}</span>
                          <span className="text-[10px] text-muted-foreground ml-2">
                            {c.end_date ? `Expires ${new Date(c.end_date).toLocaleDateString()}` : 'Continual'}
                          </span>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500" onClick={() => deleteItem('commission', c.id)} disabled={deleting === c.id}>
                          {deleting === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 items-end">
                  <div className="w-[90px]">
                    <Input type="number" min="0" step="0.01" placeholder="Amount" className="h-8 text-xs" value={rcAmount} onChange={e => setRcAmount(e.target.value)} data-testid="rc-amount" />
                  </div>
                  <div className="w-[90px]">
                    <Select value={rcDuration} onValueChange={setRcDuration}>
                      <SelectTrigger className="h-8 text-xs" data-testid="rc-duration">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="continual">Continual</SelectItem>
                        <SelectItem value="1">1 Month</SelectItem>
                        <SelectItem value="2">2 Months</SelectItem>
                        <SelectItem value="3">3 Months</SelectItem>
                        <SelectItem value="4">4 Months</SelectItem>
                        <SelectItem value="5">5 Months</SelectItem>
                        <SelectItem value="6">6 Months</SelectItem>
                        <SelectItem value="7">7 Months</SelectItem>
                        <SelectItem value="8">8 Months</SelectItem>
                        <SelectItem value="9">9 Months</SelectItem>
                        <SelectItem value="10">10 Months</SelectItem>
                        <SelectItem value="11">11 Months</SelectItem>
                        <SelectItem value="12">12 Months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Input placeholder="Reason" className="h-8 text-xs" value={rcReason} onChange={e => setRcReason(e.target.value)} data-testid="rc-reason" onKeyDown={e => { if (e.key === 'Enter') addRecurring(); }} />
                  </div>
                  <Button size="sm" className="h-8 px-3" onClick={addRecurring} disabled={rcSaving} data-testid="rc-add-btn">
                    {rcSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  </Button>
                </div>
              </div>

              <Separator />

              {/* ── SECTION 3: CLAWBACKS ── */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <h3 className="text-sm font-semibold">Clawbacks</h3>
                  {clawbackSum > 0 && <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px]">-{fmt(clawbackSum)}/wk</Badge>}
                </div>
                <p className="text-[10px] text-muted-foreground mb-2">Deducted from gross wage weekly. Auto-completes after all cycles.</p>

                {clawbacks.length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {clawbacks.map(cb => {
                      const pctDone = ((parseFloat(cb.total_amount) - parseFloat(cb.remaining_amount)) / parseFloat(cb.total_amount) * 100).toFixed(0);
                      return (
                        <div key={cb.id} className="py-2 px-2.5 rounded bg-red-50/50 border border-red-100" data-testid={`cb-item-${cb.id}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-medium text-red-700" data-role="mono">-{fmt(cb.amount_per_cycle)}/wk</span>
                              <span className="text-xs text-muted-foreground">{cb.reason}</span>
                              {cb.take_from_commissions_only && (
                                <Badge variant="outline" className="text-[9px] px-1 py-0 bg-amber-50 text-amber-700 border-amber-200">comm. only</Badge>
                              )}
                            </div>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500" onClick={() => deleteItem('clawback', cb.id)} disabled={deleting === cb.id}>
                              {deleting === cb.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                            </Button>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-1.5 rounded-full bg-red-100 overflow-hidden">
                              <div className="h-full rounded-full bg-red-400 transition-all" style={{ width: `${pctDone}%` }} />
                            </div>
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                              {fmt(cb.remaining_amount)} left · {cb.cycles_remaining} wk{cb.cycles_remaining !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Input type="number" min="0" step="0.01" placeholder="Total $" className="h-8 text-xs" value={cbTotal} onChange={e => setCbTotal(e.target.value)} data-testid="cb-total" />
                  </div>
                  <div className="w-[90px]">
                    <Select value={cbCycles} onValueChange={setCbCycles}>
                      <SelectTrigger className="h-8 text-xs" data-testid="cb-cycles">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6].map(n => (
                          <SelectItem key={n} value={String(n)}>{n} wk{n > 1 ? 's' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-[1.5]">
                    <Input placeholder="Reason" className="h-8 text-xs" value={cbReason} onChange={e => setCbReason(e.target.value)} data-testid="cb-reason" onKeyDown={e => { if (e.key === 'Enter') addClawback(); }} />
                  </div>
                  <Button size="sm" variant="destructive" className="h-8 px-3" onClick={addClawback} disabled={cbSaving} data-testid="cb-add-btn">
                    {cbSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  </Button>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    id="cb-comm-only"
                    checked={cbCommOnly}
                    onCheckedChange={(v) => setCbCommOnly(!!v)}
                    data-testid="cb-comm-only"
                  />
                  <label htmlFor="cb-comm-only" className="text-[11px] text-muted-foreground cursor-pointer select-none leading-tight">
                    Subtract from commissions only <span className="text-[10px]">(will not touch hourly pay)</span>
                  </label>
                </div>
                {cbTotal && parseInt(cbCycles, 10) > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    = {fmt(parseFloat(cbTotal || 0) / parseInt(cbCycles || 1, 10))}/week for {cbCycles} weeks
                    {cbCommOnly && <span className="text-amber-600 ml-1">(capped at commissions)</span>}
                  </p>
                )}
              </div>

              {/* ── Paid History (collapsed) ── */}
              {paidOneTime.length > 0 && (
                <>
                  <Separator />
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors">
                      {paidOneTime.length} paid one-time commission{paidOneTime.length > 1 ? 's' : ''}
                    </summary>
                    <div className="mt-1 space-y-0.5 pl-2">
                      {paidOneTime.slice(0, 10).map(c => (
                        <p key={c.id} className="text-muted-foreground">
                          {fmt(c.amount)} — {c.reason} <span className="text-[10px]">({c.week_start || 'no week'})</span>
                        </p>
                      ))}
                    </div>
                  </details>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t flex-shrink-0">
          <div className="flex items-center justify-between w-full">
            <div className="text-[10px] text-muted-foreground space-x-3">
              {oneTimeSum > 0 && <span className="text-emerald-600">+{fmt(oneTimeSum)} one-time</span>}
              {recurringSum > 0 && <span className="text-blue-600">+{fmt(recurringSum)} recurring</span>}
              {clawbackSum > 0 && <span className="text-red-600">-{fmt(clawbackSum)} clawback</span>}
            </div>
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="commission-modal-close">Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
